import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const AdminExams = () => {
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Formulaire
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [room, setRoom] = useState('');
  const [coefficient, setCoefficient] = useState(2);
  const [maxScore, setMaxScore] = useState(20);
  const [description, setDescription] = useState('');
  const [topics, setTopics] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const examsRes = await api.get('/admin/exams', { headers });
      setExams(Array.isArray(examsRes.data) ? examsRes.data : []);

      const classesRes = await api.get('/classes', { headers });
      setClasses(Array.isArray(classesRes.data) ? classesRes.data : []);

      const subjectsRes = await api.get('/subjects', { headers });
      setSubjects(Array.isArray(subjectsRes.data) ? subjectsRes.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedClass('');
    setSelectedSubject('');
    setTitle('');
    setDate('');
    setStartTime('');
    setEndTime('');
    setRoom('');
    setCoefficient(2);
    setMaxScore(20);
    setDescription('');
    setTopics('');
    setEditingId(null);
  };

  const handleEdit = (exam) => {
    setEditingId(exam.id);
    setTitle(exam.title);
    setDate(exam.date);
    setStartTime(exam.start_time || '');
    setEndTime(exam.end_time || '');
    setRoom(exam.room || '');
    setCoefficient(exam.coefficient);
    setMaxScore(exam.max_score);
    setDescription(exam.description || '');
    setTopics(exam.topics || '');
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingId && (!selectedClass || !selectedSubject)) {
      alert('Veuillez choisir une classe et une matière');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const payload = {
        title,
        date,
        start_time: startTime,
        end_time: endTime,
        room,
        coefficient: parseFloat(coefficient),
        max_score: parseFloat(maxScore),
        description,
        topics,
      };

      if (!editingId) {
        payload.class_id = selectedClass;
        payload.subject_id = selectedSubject;
        await api.post('/admin/exams', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('✅ Composition créée !');
      } else {
        await api.put(`/admin/exams/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('✅ Composition modifiée !');
      }

      resetForm();
      setShowForm(false);
      fetchData();
    } catch (err) {
      console.error('Erreur:', err);
      alert('❌ Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette composition ?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/admin/exams/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('🗑️ Composition supprimée');
      fetchData();
    } catch (err) {
      console.error('Erreur:', err);
      alert('❌ Erreur');
    }
  };

  if (loading) return <div className="p-6">Chargement...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">📅 Gérer les compositions</h1>
          <p className="text-gray-500">Créez et planifiez les compositions pour toutes les classes</p>
        </div>
        <button
          onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }}
          className="bg-[#E67E22] text-white px-4 py-2 rounded-lg hover:bg-[#D35400]"
        >
          {showForm ? 'Annuler' : '+ Nouvelle composition'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="font-bold text-[#2C3E50] mb-4">
            {editingId ? '✏️ Modifier la composition' : '📋 Nouvelle composition'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingId && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Classe *</label>
                  <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} className="w-full px-3 py-2 border rounded-lg" required>
                    <option value="">-- Choisir --</option>
                    {classes.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Matière *</label>
                  <select value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)} className="w-full px-3 py-2 border rounded-lg" required>
                    <option value="">-- Choisir --</option>
                    {subjects.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                  </select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Titre *</label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Composition 1er trimestre" className="w-full px-3 py-2 border rounded-lg" required />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Date *</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-3 py-2 border rounded-lg" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Salle</label>
                <input type="text" value={room} onChange={(e) => setRoom(e.target.value)} placeholder="Ex: 201" className="w-full px-3 py-2 border rounded-lg" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Heure début</label>
                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Heure fin</label>
                <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Barème *</label>
                <select value={maxScore} onChange={(e) => setMaxScore(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                  <option value="10">/10</option>
                  <option value="20">/20</option>
                  <option value="100">/100</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Coefficient *</label>
                <input type="number" value={coefficient} onChange={(e) => setCoefficient(e.target.value)} min="0.5" step="0.5" className="w-full px-3 py-2 border rounded-lg" required />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">📚 Programme à réviser</label>
                <textarea value={topics} onChange={(e) => setTopics(e.target.value)} rows="3" placeholder="Ex: Chapitres 1 à 3" className="w-full px-3 py-2 border rounded-lg" />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Description / Instructions</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows="2" className="w-full px-3 py-2 border rounded-lg" />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button type="button" onClick={() => { resetForm(); setShowForm(false); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                Annuler
              </button>
              <button type="submit" disabled={saving} className="px-6 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] disabled:opacity-50">
                {saving ? 'Enregistrement...' : (editingId ? '💾 Modifier' : '📅 Planifier')}
              </button>
            </div>
          </form>
        </div>
      )}

      {exams.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-500">
          <p className="text-lg mb-2">📭 Aucune composition planifiée</p>
          <p className="text-sm">Cliquez sur "Nouvelle composition" pour commencer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {exams.map((exam) => (
            <div key={exam.id} className="bg-white rounded-xl shadow-md p-5 border-l-4 border-purple-500">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-bold text-[#2C3E50]">{exam.title}</h3>
                  <p className="text-sm text-gray-500">{exam.class_name} • {exam.subject}</p>
                </div>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                  Coef. {exam.coefficient}
                </span>
              </div>

              <div className="space-y-1 text-sm mb-3">
                <p>📅 <strong>{exam.date_display}</strong></p>
                {exam.start_time && <p>🕐 {exam.start_time}{exam.end_time && ` - ${exam.end_time}`}</p>}
                {exam.room && <p>🏫 Salle {exam.room}</p>}
              </div>

              {exam.topics && (
                <div className="mb-3 p-2 bg-gray-50 rounded text-sm">
                  <p className="text-xs text-gray-500">📚 Programme :</p>
                  <p>{exam.topics}</p>
                </div>
              )}

              <div className="flex space-x-2 mt-3">
                <button onClick={() => handleEdit(exam)} className="flex-1 text-sm py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100">
                  ✏️ Modifier
                </button>
                <button onClick={() => handleDelete(exam.id)} className="flex-1 text-sm py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100">
                  🗑️ Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminExams;